import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Upload, message, Space, Popconfirm, List, Typography } from 'antd';
import { ArrowRightOutlined, UploadOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined, FileOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { faqsApi, show422Error, normalizeFaqApiBody, faqDescriptionToPlainText } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { FormSkeleton } from '../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const { Option } = Select;
const { Link, Text } = Typography;

const normFiles = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList || [];
};

const SuperadminFaqEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/settings/superadmin-faqs', { state: buildGoBackState() });
  const goToShow = () => {
    navigate(`/settings/superadmin-faqs/${id}`, {
      state: {
        returnTo: returnToRef.current,
        ...buildGoBackState(),
      },
    });
  };
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [faq, setFaq] = useState(null);
  const [categories, setCategories] = useState([]);
  const [fileActionId, setFileActionId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await faqsApi.getEditMeta(id);
        const root = normalizeFaqApiBody(res);
        if (cancelled) return;
        setFaq(root.faq || null);
        setCategories(root.categories || []);
        const f = root.faq || {};
        form.setFieldsValue({
          title: f.title || '',
          category_id: f.faq_category_id ?? f.category?.id,
          description: faqDescriptionToPlainText(f.description),
          files: [],
        });
      } catch (error) {
        if (!cancelled) message.error(error?.message || 'خطا در دریافت اطلاعات');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, form]);

  const categoryIdDefault = useMemo(() => faq?.faq_category_id ?? faq?.category?.id, [faq]);

  const coverImageSrc = useMemo(() => {
    if (!faq) return null;
    if (faq.image_url) return faq.image_url;
    if (faq.image && /^https?:\/\//i.test(String(faq.image))) return faq.image;
    return null;
  }, [faq]);

  const onFinish = async (values) => {
    const fd = new FormData();
    fd.append('title', values.title?.trim() || '');
    fd.append('description', values.description || '');
    fd.append('category_id', String(values.category_id ?? categoryIdDefault ?? ''));
    const files = values.files || [];
    files.forEach((f) => {
      const file = f?.originFileObj || f;
      if (file && file instanceof Blob) fd.append('file[]', file);
    });

    setSubmitting(true);
    try {
      await faqsApi.update(id, fd);
      message.success('سوال بروزرسانی شد');
      goToShow();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  const onDeleteFile = async (fileId) => {
    setFileActionId(fileId);
    try {
      await faqsApi.deleteFile(fileId);
      message.success('فایل حذف شد');
      const res = await faqsApi.getEditMeta(id);
      const root = normalizeFaqApiBody(res);
      setFaq(root.faq || null);
    } catch (error) {
      message.error(error?.message || 'خطا در حذف فایل');
    } finally {
      setFileActionId(null);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/25">
                <EditOutlined className="text-xl text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">ویرایش سوال</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">به‌روزرسانی عنوان، دسته‌بندی و فایل‌ها</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ArrowRightOutlined />} onClick={goToShow}>
                مشاهده
              </Button>
              <Button icon={<QuestionCircleOutlined />} onClick={goBackToList}>
                لیست سوالات
              </Button>
            </Space>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {loading ? (
                <FormSkeleton fields={7} />
              ) : (
                <>
                  
                  <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item name="title" label="عنوان" rules={[{ required: true, message: 'عنوان را وارد کنید' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="category_id" label="دسته‌بندی" rules={[{ required: true, message: 'دسته‌بندی را انتخاب کنید' }]}>
                      <Select popupClassName="custom-font" showSearch optionFilterProp="children" placeholder="انتخاب دسته‌بندی">
                        {categories.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="description" label="توضیحات" rules={[{ required: true, message: 'توضیحات را وارد کنید' }]}>
                      <Input.TextArea rows={6} placeholder="متن ساده (بدون HTML)" />
                    </Form.Item>

                    <Form.Item name="files" label="افزودن فایل جدید" valuePropName="fileList" getValueFromEvent={normFiles}>
                      <Upload multiple beforeUpload={() => false}>
                        <Button icon={<UploadOutlined />}>انتخاب فایل‌ها</Button>
                      </Upload>
                    </Form.Item>

                    {coverImageSrc ? (
                    <div className="mb-6 min-w-0 rounded-xl border border-gray-100 bg-white/80 p-3 dark:border-gray-700 dark:bg-gray-900/30">
                      <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">تصویر فعلی</div>
                      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
                        <img
                          src={coverImageSrc}
                          alt=""
                          className="mx-auto block max-h-56 w-full max-w-lg object-contain"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 rounded-lg border border-dashed border-gray-200 px-3 py-3 text-center text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
                      تصویر اصلی برای این سوال ثبت نشده است
                    </div>
                  )}

                    <Form.Item className="mb-0">
                      <Space wrap>
                        <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS}>
                          ذخیره تغییرات
                        </Button>
                        <Button onClick={goToShow}>انصراف</Button>
                      </Space>
                    </Form.Item>
                  </Form>

                  <div className="mt-6 border-t border-gray-100 pt-4 dark:border-gray-700">
                    <div className="text-xs text-gray-500 mb-2">فایل‌های فعلی</div>
                    <List
                      dataSource={faq?.files || []}
                      locale={{ emptyText: 'فایلی ثبت نشده است' }}
                      renderItem={(f) => (
                        <List.Item
                          key={f.id}
                          className="!px-0"
                          actions={[
                            <Popconfirm key="del" title="حذف فایل؟" okText="حذف" cancelText="لغو" onConfirm={() => onDeleteFile(f.id)}>
                              <Button type="text" danger icon={<DeleteOutlined />} loading={fileActionId === f.id} aria-label="حذف فایل" />
                            </Popconfirm>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<FileOutlined className="text-gray-400" />}
                            title={
                              <Link href={f.file_url} target="_blank" rel="noreferrer">
                                {f.filename || `file-${f.id}`}
                              </Link>
                            }
                            description={<Text type="secondary">{f.size ? `${Number(f.size).toLocaleString('fa-IR')} بایت` : '—'}</Text>}
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                </>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminFaqEdit;
